import * as React from 'react';
import { RaterApprovalRater } from './models';
import LocalDiv from '../localization/LocalDiv';
import { FLEX_COLUMN, FLEX_ROW } from '../layout/flexBox';
import RaterApprovalList from './RaterApprovalList';
import { TextDirection } from '../localization/textDirection';
import { css } from '@emotion/core';
import { COLORS } from '../globalStyle/colors';
import LocalBtn from '../localization/LocalBtn';
import { Str } from '../localization/translationKeys';
import { btnStyle } from '../forms/styles/button';
import { faEnvelope, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Spacer from '../layout/Spacer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Msg from '../localization/Msg';
import { RaterLevel } from '../project/survey/raters/models';
import AddRaterForm from '../project/survey/raters/AddRaterForm';
import { HIDE_ON_MOBILE } from '../globalStyle/mediaQueries';
import { Map } from '../language/models';


const styles = {
  root:                 FLEX_COLUMN,
  toolbar:              [
    FLEX_ROW,
    css`
      min-height: 35px;
      padding: 15px 12px;
    `,
  ],
  msgBar:               [
    css`
      justify-content: space-between;
      background-color: ${COLORS.WHITE};
    `,
  ],
  actionsBar:           [
    css`
      background-color: ${COLORS.LIGHTEST_GREY};
      border-top: 1px solid ${COLORS.LIGHTER_GREY};
      justify-content: space-between;
    `,
  ],
  msgContainer:         [
    FLEX_ROW,
    css`
      align-items: center;
    `,
  ],
  approverMsg:          css`
    color: ${COLORS.GREEN};
  `,
  errorMsg:             css`
    color: ${COLORS.RED};
  `,
  btnContainer:         [
    FLEX_ROW,
    HIDE_ON_MOBILE,
  ],
  button:               css`
    width: max-content;
    padding-left: 12px;
    padding-right: 12px;
  `,
  customizeApprovalBtn: btnStyle.secondary(COLORS.GREEN),
  sendApprovalBtn:      btnStyle.primary(COLORS.GREEN),
};


type Props = {
  className?: string;

  raters: RaterApprovalRater[];
  raterLevels: RaterLevel[];

  ratersToApprove: Map<RaterApprovalRater>;
  ratersToDecline: Map<RaterApprovalRater>;

  textDirection: TextDirection;
  participantName: string;

  showNameFields: boolean;
  requireNamesOnAdd: boolean;

  addErrorMsg?: string;
  ratersNotUpdatedCount: number;
  showProcessingErrorMsg: boolean;
  showApprovalSentMsg: boolean;

  disableCustomizeModalButton: boolean;
  disableSendApprovalButton: boolean;

  toggleCustomizeEmailModal: () => void;
  setEmailAddress: (emailAddress: string) => void;
  setAddErrorMsg: (errorMsg?: string) => void;

  addRater: (levelId: number, email: string, firstName: string, lastName: string) => void;
  markRaterApproved: (rater: RaterApprovalRater) => void;
  markRaterDeclined: (rater: RaterApprovalRater) => void;
  sendApprovalEmail: () => void;
};


/**
 * RaterApprovalTable: a component that renders a list of raters for a given project and participant, as well as tools
 * to enable the user to add, approve, or decline raters
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterApprovalTable: React.FC<Props> = props => {
  const { notifMsg, msgStyle, msgData } = React.useMemo(
    () => ({
      notifMsg:
        props.showProcessingErrorMsg
          ? Str.UnableToProcess
          : !!props.ratersNotUpdatedCount
            ? Str.RatersNotProcessed
            : !!props.addErrorMsg
              ? ''
              : props.showApprovalSentMsg
                ? Str.ApprovalSent
                : props.raters.some(i => i.approvalStatus === 0 || i.approvalStatus === 2)
                  ? Str.RaterApprovalMsg
                  : '',
        msgStyle:
        props.showProcessingErrorMsg || !!props.ratersNotUpdatedCount || !!props.addErrorMsg
          ? styles.errorMsg
          : styles.approverMsg,
      msgData:
        !!props.ratersNotUpdatedCount
          ? { 'number': props.ratersNotUpdatedCount }
          : { 'firstLast': `${props.participantName}` },
    }),
    [
      props.addErrorMsg,
      props.participantName,
      props.ratersNotUpdatedCount,
      props.showApprovalSentMsg,
      props.showProcessingErrorMsg,
      props.raters,
    ],
  );

  const showIcon  = React.useMemo(() => notifMsg !== '', [notifMsg]);

  return (
    <LocalDiv css={styles.root}>
      <LocalDiv css={[styles.toolbar, styles.msgBar]}>
        <LocalDiv css={[styles.msgContainer, msgStyle]}>
          {
            (showIcon) && (
              <FontAwesomeIcon icon={faExclamationTriangle} size={'2x'} />
            )
          }
          <Spacer sizePx={10} />
          <Msg
            str={notifMsg}
            defaultMsg={props.addErrorMsg}
            replace={msgData}
          />
        </LocalDiv>
        <LocalDiv css={styles.btnContainer}>
          <LocalBtn
            str={Str.CustomizeEmail}
            css={[styles.button, styles.customizeApprovalBtn]}
            onClick={props.toggleCustomizeEmailModal}
            disabled={props.disableCustomizeModalButton}
          />
          <Spacer sizePx={8} />
          <LocalBtn
            str={Str.SendApproval}
            icon={faEnvelope}
            css={[styles.button, styles.sendApprovalBtn]}
            onClick={props.sendApprovalEmail}
            disabled={props.disableSendApprovalButton}
          />
        </LocalDiv>
      </LocalDiv>
      <LocalDiv css={[styles.toolbar, styles.actionsBar]}>
        <AddRaterForm
          raterLevels={props.raterLevels}
          addRater={props.addRater}
          setEmailAddress={props.setEmailAddress}
          setErrorMsg={props.setAddErrorMsg}
          showNameFields={props.showNameFields}
          requireNamesOnAdd={props.requireNamesOnAdd}
          maximumsRequired={false}
          approver={true}
        />
      </LocalDiv>
      <RaterApprovalList
        raters={props.raters}
        textDirection={props.textDirection}
        ratersToApprove={props.ratersToApprove}
        ratersToDecline={props.ratersToDecline}
        showRaterNames={props.showNameFields}
        markRaterApproved={props.markRaterApproved}
        markRaterDeclined={props.markRaterDeclined}
      />
    </LocalDiv>
  );
};

export default RaterApprovalTable;